import { InboxItem, DashboardData } from './dashboardTypes';

/**
 * Truncates a string to the specified length and adds ellipsis
 */
function truncate(str: string, length: number): string {
  if (!str) return '';
  if (str.length <= length) return str;
  return str.substring(0, length) + '...';
}

/**
 * Transforms dashboard data (topics and matches) into unified inbox items
 * @param data - Dashboard data containing topics and matches
 * @returns Array of inbox items sorted by priority and timestamp
 */
export function processInboxItems(data: DashboardData): InboxItem[] {
  const items: InboxItem[] = [];

  // Process unread messages from job topics
  if (data.jobTopics && Array.isArray(data.jobTopics)) {
    data.jobTopics.forEach((topic: any) => {
      // Only show unresolved topics as inbox items
      if (!topic.isResolvedByOwner) {
        items.push({
          id: `job-topic-${topic.id}`,
          type: 'message',
          priority: 'high', // Unread messages are high priority
          title: `Message on ${topic.jobTitle || 'role'}`,
          message: truncate(topic.message, 60),
          timestamp: topic.lastCommentCreatedAt || topic.createdAt,
          link: `/messages?topicId=${topic.id}&type=job`,
          icon: 'MessageSquare',
        });
      }
    });
  }

  // Process unread messages from candidate topics
  if (data.candidateTopics && Array.isArray(data.candidateTopics)) {
    data.candidateTopics.forEach((topic: any) => {
      // Only show unresolved topics as inbox items
      if (!topic.isResolvedByOwner) {
        const candidateTitle = topic.jobTitle || topic.idealJobTitles?.[0] || 'candidate';
        items.push({
          id: `candidate-topic-${topic.id}`,
          type: 'message',
          priority: 'high', // Unread messages are high priority
          title: `Message on ${candidateTitle}`,
          message: truncate(topic.message, 60),
          timestamp: topic.lastCommentCreatedAt || topic.createdAt,
          link: `/messages?topicId=${topic.id}&type=candidate`,
          icon: 'MessageSquare',
        });
      }
    });
  }

  // Process new matches from job matches (role perspective)
  if (data.jobMatches && Array.isArray(data.jobMatches)) {
    data.jobMatches.forEach((match: any) => {
      // Show matches that are pending or newly created (within last 7 days)
      const isNew = isRecentlyCreated(match.createdAt, 7);
      const isPending = match.currentStatus === 'pending' || match.currentStatus === 'new';

      if (isPending || isNew) {
        const candidateTitle = match.candidateJobTitle || match.candidateIdealJobTitles?.[0] || 'Candidate';
        items.push({
          id: `job-match-${match.id}`,
          type: 'match',
          priority: isPending ? 'high' : 'normal',
          title: 'New match to review',
          message: `${candidateTitle} for ${match.jobTitle || 'role'}`,
          timestamp: match.createdAt,
          link: `/matches?matchId=${match.id}`,
          icon: 'UserCheck',
        });
      }
    });
  }

  // Process new matches from candidate matches (candidate perspective)
  if (data.candidateMatches && Array.isArray(data.candidateMatches)) {
    data.candidateMatches.forEach((match: any) => {
      // Show matches that are pending or newly created (within last 7 days)
      const isNew = isRecentlyCreated(match.createdAt, 7);
      const isPending = match.currentStatus === 'pending' || match.currentStatus === 'new';

      if (isPending || isNew) {
        items.push({
          id: `candidate-match-${match.id}`,
          type: 'match',
          priority: isPending ? 'high' : 'normal',
          title: 'New match to review',
          message: `${match.candidateName || 'Candidate'} for ${match.jobTitle || 'role'}`,
          timestamp: match.createdAt,
          link: `/matches?matchId=${match.id}`,
          icon: 'UserCheck',
        });
      }
    });
  }

  // Sort inbox items:
  // 1. High priority first
  // 2. Then by timestamp (newest first)
  return items.sort((a, b) => {
    // Priority comparison
    if (a.priority !== b.priority) {
      return a.priority === 'high' ? -1 : 1;
    }

    // Timestamp comparison (newest first)
    const dateA = new Date(a.timestamp).getTime();
    const dateB = new Date(b.timestamp).getTime();
    return dateB - dateA;
  });
}

/**
 * Checks if a timestamp is within the specified number of days
 */
function isRecentlyCreated(timestamp: string, days: number): boolean {
  if (!timestamp) return false;

  const now = new Date();
  const created = new Date(timestamp);
  const diffInMs = now.getTime() - created.getTime();
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  return diffInDays <= days;
}

/**
 * Gets the count of high priority inbox items
 */
export function getHighPriorityCount(items: InboxItem[]): number {
  return items.filter(item => item.priority === 'high').length;
}

/**
 * Gets the count of unread messages
 */
export function getUnreadMessagesCount(items: InboxItem[]): number {
  return items.filter(item => item.type === 'message').length;
}

/**
 * Gets the count of pending matches
 */
export function getPendingMatchesCount(items: InboxItem[]): number {
  return items.filter(item => item.type === 'match' && item.priority === 'high').length;
}
